import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  DollarSign, ArrowLeft, MessageSquare, Tag, CheckCircle, 
  Clock, XCircle, List, Home, QrCode, LogOut
} from 'lucide-react';
import Footer from '../components/Footer';

export default function DashboardTipsPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    let cancelled = false;
    const fetchTips = async () => {
      try {
        const response = await fetch(`${API}/tips`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setTips(data);
        }
      } catch (error) {
        console.error('Failed to fetch tips:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTips();
    return () => { cancelled = true; };
  }, [API, token]);

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'created':
        return <Clock size={16} className="text-amber-500" />;
      case 'failed':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-[#52525B]" />;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 md:hidden">
        <div className="flex items-center justify-between">
          <h1 className="font-heading font-bold text-lg text-[#F5C542]">Golden Tips</h1>
          <button onClick={handleLogout} className="text-[#A1A1AA] hover:text-white">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-64 h-screen sticky top-0 bg-[#0a0a0a] border-r border-white/5 p-6">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-xl text-[#F5C542]">Golden Tips</h1>
          </div>

          <nav className="flex-1 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors">
              <Home size={20} />
              <span>Dashboard</span>
            </Link>
            <Link to="/dashboard/tips" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 text-white">
              <List size={20} />
              <span>Tips History</span>
            </Link>
          </nav>

          <div className="pt-6 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
                {user?.photoUrl ? (
                  <img src={user.photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-bold">{user?.displayName?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{user?.displayName}</p>
                <p className="text-xs text-[#A1A1AA] truncate">{user?.serviceTitle}</p>
              </div>
            </div>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8" data-testid="tips-page-main">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Link to="/dashboard" className="md:hidden text-[#A1A1AA] hover:text-white">
                <ArrowLeft size={24} />
              </Link>
              <h2 className="text-2xl font-heading font-bold">Tips History</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            ) : tips.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <DollarSign size={32} className="text-[#52525B]" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No tips yet</h3>
                <p className="text-[#A1A1AA] mb-6">Share your Golden Tips link to start receiving tips!</p>
                <Link to="/dashboard" className="btn-secondary inline-flex items-center gap-2">
                  <QrCode size={18} />
                  Get your QR code
                </Link>
              </div>
            ) : (
              <div className="space-y-3" data-testid="tips-list">
                {tips.map((tip) => (
                  <div key={tip.id} className="tip-list-item" data-testid={`tip-item-${tip.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#F5C542]/10 flex items-center justify-center">
                        <DollarSign size={20} className="text-[#F5C542]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-lg">
                            {formatCurrency(tip.amountCents)}
                          </span>
                          {getStatusIcon(tip.status)}
                        </div>
                        <p className="text-sm text-[#A1A1AA]">{formatDate(tip.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {tip.praiseTag && (
                        <span className="flex items-center gap-1 text-xs text-[#F5C542] bg-[#F5C542]/10 px-2 py-1 rounded-full">
                          <Tag size={12} />
                          {tip.praiseTag}
                        </span>
                      )}
                      {tip.note && (
                        <span className="flex items-center gap-1 text-xs text-[#A1A1AA]">
                          <MessageSquare size={12} />
                          Note
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Footer className="mt-10 max-w-3xl mx-auto" />
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 px-4 py-2 flex items-center justify-around z-50">
        <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <Home size={20} />
          <span className="text-xs">Home</span>
        </Link>
        <Link to="/dashboard/tips" className="flex flex-col items-center gap-1 py-2 px-4 text-[#F5C542]">
          <List size={20} />
          <span className="text-xs">Tips</span>
        </Link>
        <Link to={`/u/${user?.publicSlug}`} target="_blank" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <QrCode size={20} />
          <span className="text-xs">My Page</span>
        </Link>
      </nav>
    </div>
  );
}
